import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';

import { MaterialTopTabNavigationOptions, createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { useNavigation } from '@react-navigation/native';

import { CashBalanceButton } from '@/components/BalanceButton';
import { KeyboardAvoidingViewNoAnimation } from '@/components/KeyboardAvoidingViewNoAnimation';
import { SheetScreen } from '@/components/ScreenComponent';
import { SocialCurrencyToggle } from '@/components/SocialCurrencyToggle';
import { SubmissionOverlay } from '@/components/checkout/SubmissionOverlay';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { CheckoutHeader } from '@/feature/betslip-pickem/components/CheckoutHeader';
import { SocialWalletBetSlipBalanceButton } from '@/feature/betslip-sbk/components/SocialWalletBetSlipBalanceButton';
import { useResumeBetFailsafe } from '@/feature/betslip-sbk/hooks/use-resume-bet-failsafe';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useAuthUserSettings } from '@/hooks/use-auth-user-settings';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { Product } from '@/hooks/use-jurisdiction';
import { usePreventAndroidBackNavigation } from '@/hooks/use-prevent-android-back-navigation';
import { common, designSystem } from '@/styles/styles';
import { runStoreUpdate } from '@/utils/zustand';

import { BetSlipActionTray } from '../components/betslip-action-tray/BetSlipActionTray';
import { ComboTabScreen } from '../components/betslip-tabs/ComboTab';
import { PredictionsTabScreen } from '../components/betslip-tabs/PredictionsTab';
import { SinglesTabScreen } from '../components/betslip-tabs/SinglesTab';
import { SbkEntrySummaryModalProvider } from '../components/entry-summary-modal';
import { OddsChangeModalProvider } from '../components/odds-change-modal/OddsChangeModalProvider';
import { PicksRemovedModalProvider } from '../components/picks-removed-modal/PicksRemovedModalProvider';
import { useBetSlipInitialTab, useIsBetSlipSubmitting } from '../hooks/betslip-hooks';
import { useClosedSelectionsToast } from '../hooks/use-closed-selections-toast';
import { getSbkBetSlipStore, useSbkBetSlipStore } from '../hooks/use-sbk-betslip-store';
import { useSyncBetslipPromos } from '../hooks/use-sync-betslip-promos';
import { useTrackToggleBetSlip } from '../hooks/use-track-toggle-bet-slip';
import { BetSlipParamsList, BetSubmissionStatus } from '../types';
import { selectionCountSelector } from '../utils/betslip-selectors';

const Tab = createMaterialTopTabNavigator<BetSlipParamsList>();

const BETSLIP_TAB_BAR_CONFIG: MaterialTopTabNavigationOptions = {
    tabBarStyle: {
        backgroundColor: 'transparent',
        shadowColor: 'auto',
        shadowOpacity: 0,
        shadowRadius: 0,
        elevation: 0,
        borderBottomWidth: 1,
        borderBottomColor: ds.colorValue.border.strong,
    },
    tabBarItemStyle: {
        paddingTop: 4,
        height: 44,
        justifyContent: 'flex-start',
    },
    tabBarLabelStyle: {
        fontSize: 14,
        lineHeight: 20,
        fontWeight: '600',
        letterSpacing: -0.15,
    },
    tabBarContentContainerStyle: {
        paddingHorizontal: 16,
    },
    tabBarIndicatorStyle: {
        backgroundColor: ds.colorValue.background.neutral.inverse,
        height: 2,
        borderRadius: 1,
    },
    tabBarPressColor: 'transparent',
    tabBarActiveTintColor: ds.colorValue.text.primary,
    tabBarInactiveTintColor: designSystem.colors.neutral300,
};

export const BetSlipScreen = () => {
    return (
        <PicksRemovedModalProvider>
            <BetSlipContent />
        </PicksRemovedModalProvider>
    );
};

const BetSlipContent = () => {
    useResumeBetFailsafe();
    useClosedSelectionsToast();
    useSyncBetslipPromos();
    const initialTab = useBetSlipInitialTab();
    const isBetSlipSubmitting = useIsBetSlipSubmitting();

    // update user settings with latest data when user open the bet slip
    const updateUserSettings = useSbkBetSlipStore(state => state.actions.updateUserSettings);
    const { data: userSettingsData } = useAuthUserSettings();
    useEffect(() => {
        if (userSettingsData) {
            updateUserSettings(userSettingsData);
        }
    }, [updateUserSettings, userSettingsData]);

    // Close bet slip if there are no selections and no closed selections pending removal
    const hasSelections = useSbkBetSlipStore(
        state => selectionCountSelector(state) > 0 || state.closedSelections.length > 0
    );
    const { goBack } = useNavigation();
    const isClosingRef = React.useRef(false);

    useEffect(() => {
        if (!hasSelections && !isClosingRef.current) {
            isClosingRef.current = true;
            goBack();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasSelections]); // Intentionally exclude goBack - it's unstable can cause infinite loops

    usePreventAndroidBackNavigation(isBetSlipSubmitting);

    const { t } = useTranslation('betslip_sbk');
    const { screenListeners } = useTrackToggleBetSlip();
    const hasSocialWallet = useHasSocialWallet();
    const isPredictions = useIsPredictionsProduct();

    // Predictions selections only live while the slip is open: closing it removes them.
    // Submissions are exempt so polling and the success screen keep their data.
    useEffect(() => {
        if (!isPredictions) {
            return;
        }
        return () => {
            const { betSubmissionStatus, actions } = getSbkBetSlipStore(Product.Prediction).getState();
            if (betSubmissionStatus === BetSubmissionStatus.Idle || betSubmissionStatus === BetSubmissionStatus.Error) {
                runStoreUpdate(actions.clearBetSlip);
            }
        };
    }, [isPredictions]);

    return (
        <SheetScreen style={common.flex} edges={['top']}>
            <OddsChangeModalProvider>
                <SbkEntrySummaryModalProvider>
                    <MaxWidthWrapper>
                        <CheckoutHeader
                            isSubmitting={isBetSlipSubmitting}
                            rightItem={
                                hasSocialWallet ? (
                                    <Box pr="s12">
                                        <SocialCurrencyToggle />
                                    </Box>
                                ) : null
                            }
                            balanceButton={
                                hasSocialWallet ? <SocialWalletBetSlipBalanceButton /> : <CashBalanceButton />
                            }
                        />
                    </MaxWidthWrapper>

                    <KeyboardAvoidingViewNoAnimation
                        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
                        style={common.flex}
                    >
                        <Box mt="s8" flex={1} accessible={false}>
                            {isPredictions ? (
                                <PredictionsTabScreen />
                            ) : (
                                <Tab.Navigator
                                    screenListeners={screenListeners}
                                    initialRouteName={initialTab}
                                    screenOptions={BETSLIP_TAB_BAR_CONFIG}
                                >
                                    <Tab.Screen
                                        name={'Parlays'}
                                        options={{ title: t('parlays_tab'), tabBarButtonTestID: 'parlaysTab' }}
                                    >
                                        {() => <ComboTabScreen />}
                                    </Tab.Screen>
                                    <Tab.Screen
                                        name={'Singles'}
                                        options={{ title: t('singles_tab'), tabBarButtonTestID: 'singlesTab' }}
                                    >
                                        {() => <SinglesTabScreen />}
                                    </Tab.Screen>
                                </Tab.Navigator>
                            )}
                        </Box>
                    </KeyboardAvoidingViewNoAnimation>
                    {isBetSlipSubmitting ? <SubmissionOverlay /> : null}
                    <BetSlipActionTray />
                </SbkEntrySummaryModalProvider>
            </OddsChangeModalProvider>
        </SheetScreen>
    );
};
